<template>
  <div>
    <div class="row" style="background-color: #f7f7fc; padding: 10px 0px; border-top-left-radius: 1rem; border-top-right-radius: 1rem;">
      <div :class="isAdmin ? 'col-sm-10' : 'col-sm-12'">
        <div class="input-group" style="height: 85%; width: 90%; float: left;">
          <input id="filter-text-box-invited" data-test="filter-text-box-invited" type="text" :placeholder="$t('search-invited-users')" class="form-control border-0 shadow-sm" @input="onFilterTextBoxChanged()">
          <span class="input-group-text border-0 shadow-sm"><i class="fa fa-search"></i></span>
        </div>
        <span class="text-primary" style="cursor: pointer; float: right; width: 8%; padding-top: 9px;" @click="refreshFilterTextBox()">{{ $t('clear-search') }}</span>
      </div>
      <div v-if="isAdmin" class="col-sm-2" style="text-align: right;">
        <button id="resend_invites" data-test="resend_invites" class="btn btn-ml btn-outline-primary" style="border: 2px solid;" @click="resend(selectedRows)">
          {{ $t('network.member.resendInvites') }}
          <span v-if="selectedRows.length">({{ selectedRows.length }})</span>
        </button>
      </div>
    </div>
    <b-overlay :show="busy" rounded="sm">
      <ag-grid-vue
        style="width: 100%; height: 100vh; font-family: 'Nunito', sans-serif;"
        class="ag-theme-alpine text-center"
        rowSelection="multiple"
        rowMultiSelectWithClick=true
        :gridOptions="gridOptions"
        :frameworkComponents="frameworkComponents"
        :columnDefs="columnDefs"
        :pagination="true"
        paginationPageSize="30"
        :rowData="filteredInvitedMembers"
        @selection-changed="onSelectionChanged"
        @grid-ready="onGridReady"
      ></ag-grid-vue>
    </b-overlay>

    <b-modal v-model="showingInviteModal" hide-footer hide-header size="lg">
      <InviteDetailsModal :network_slug="network.slug" :invite="selectedMemberInvite" @closeInviteModal="showingInviteModal = false" />
    </b-modal>

    <b-modal v-model="showResendModal" hide-footer hide-header size="lg">
      <span role="button" class="close float-end fs-4 mt-n2 me-n1" @click="showResendModal = !showResendModal">
        <span aria-hidden="true"><i class="fa fa-times"></i></span>
      </span>
      <p class="fs-5 fw-semibold pb-3" style="border-bottom: 1px solid #e9e9f2;">{{ $t('network.member.resendBunchOfInvites') }}</p>
      <p class="lead">
        <i class="fa fa-bomb"></i>
        {{ $t('network.member.heyThere') }}
      </p>
      <b-alert variant="warning" :show="selectedRows.length > 0">
        <!--More than one person selected-->
        <span v-if="selectedRows.length > 1">
          <!-- Category 1: More than zero people with pending invites -->
          <span v-if="(selectedRows.filter(invite => !invite.accepted)).length > 0">
            <!-- Subcategory 1A: Multiple people with pending invites -->
            <span v-if="(selectedRows.filter(invite => !invite.accepted)).length > 1">
              <!-- Sub-subcategory 1A1: No people with accepted invites -->
              <span v-if="(selectedRows.filter(invite => invite.accepted)).length === 0">
                {{ $t('you-are-sending-x-re-invites', { number: (selectedRows.filter(invite => !invite.accepted)).length }) }}
              </span>
              <!-- Sub-subcategory 1A2: Only one person with accepted invite -->
              <span v-else-if="(selectedRows.filter(invite => invite.accepted)).length === 1">
                {{ $t('from-the-selected-list-one-person-is-already-a-member-of-the-network-and-x-have-previously-received-invites-only-pending-invites-will-be-resent', { number: (selectedRows.filter(invite => !invite.accepted)).length }) }}
              </span>
              <!-- Sub-subcategory 1A3: Multiple people with accepted invites -->
              <span v-else-if="(selectedRows.filter(invite => invite.accepted)).length > 1">
                {{ $t('from-the-selected-list-x-are-already-members-of-this-network-and-x-have-previously-received-invites-only-pending-invites-will-be-resent',
                { number1: (selectedRows.filter(invite => invite.accepted)).length, number2: (selectedRows.filter(invite => !invite.accepted)).length }) }}
              </span>
            </span>
            <!-- Subcategory 1B: Single person with pending invite -->
            <span v-else>
              <!-- Sub-subcategory 1B1: Only one person with an accepted invite -->
              <span v-if="(selectedRows.filter(invite => invite.accepted)).length === 1">
                {{ $t('from-the-selected-list-one-person-is-already-a-member-of-the-network-and-one-has-previously-received-an-invite-only-pending-invites-will-be-resent') }}
              </span>
              <!-- Sub-subcategory 1B2: More than one person with an accepted invite -->
              <span v-else-if="(selectedRows.filter(invite => invite.accepted)).length > 1">
                {{ $t('from-the-selected-list-one-person-has-previously-received-an-invite-and-x-are-already-members-of-this-network-only-pending-invites-will-be-resent', { number: (selectedRows.filter(invite => invite.accepted)).length }) }}
              </span>
            </span>
          </span>
          <!-- Category 2: Zero people with pending invites and multiple accepted invites -->
          <span v-else-if="(selectedRows.filter(invite => !invite.accepted)).length === 0">
            {{ $t('you-cannot-send-re-invites-to-existing-members') }}
          </span>
          <!-- Category 3: More than zero people with accepted invites (already covered) -->
          <!-- Category 4: Zero people with accepted invites (already covered) -->
        </span>
        <span v-else>
          <span v-if="(selectedRows.filter(invite => invite.accepted)).length > 0">
            <!--A single person with an accepted invite-->
            {{ $t('this-person-is-already-a-member-of-the-network') }}
            {{ $t('you-cannot-send-re-invites-to-existing-members') }}
          </span>
          <!--Only one person selected-->
          <span v-else>
            <!--A single person with a pending invite-->
            {{ $t('you-are-sending-1-re-invite') }}
          </span>
        </span>
      </b-alert>
      <b-alert variant="warning" :show="selectedRows.length == 0">
        {{ $t('network.member.reInvitationInfo', { number: (this.invitedMembers.filter(invite => !invite.accepted)).length }) }}
      </b-alert>

      <b-form-textarea
        id="invite_message"
        v-model="inviteMessage"
        data-test="invite_message"
        :placeholder="$t('network.member.newInvitationMessage')"
        rows="3"
        max-rows="6"
      ></b-form-textarea>

      <div class="text-center">
        <button id="resend_invitations" data-test="resend_invitations" :disabled="!busy && disabled" class="btn btn-lg btn-block btn-primary mt-4" @click="resendAll(selectedRows)">
          {{ $t('network.member.resendInvitations') }}
          <i v-show="busy" class="fas fa-spinner fa-spin"></i>
        </button>
      </div>
    </b-modal>
  </div>
</template>
<script>
import InviteDetailsModal from './InviteDetailsModal.vue';
import BtnInviteDetails from './BtnInviteDetails.js';
import BtnDeleteInvite from './BtnDeleteInvite.js';
import '@/lib/ag-grid';

export default {
  components: {
    InviteDetailsModal,
  },
  props: {
    network: {
      type: Object,
      default: null,
    },
    filteredMembers: {
      type: Array,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inviteMessage: '',
      showResendModal: false,
      showingInviteModal: false,
      selectedMemberInvite: {},
      invitedMembers: [],
      filteredInvitedMembers: [],
      loadingInvites: true,
      processingResend: false,
      gridOptions: null,
      frameworkComponents: null,
      columnDefs: null,
      rowData: null,
      selectedRows: [],
      selectedUser: [],
      disabled: false,
    };
  },
  computed: {
    busy() {
      return this.processingResend || this.loadingInvites;
    },
  },
  watch: {
    filteredMembers: {
      handler(newV) {
        this.filteredInvitedMembers = newV;
      },
    },
    selectedRows: {
      handler(newV) {
        if (newV.length > 0 && (newV.length == newV.filter(invite => invite.accepted).length)) {
          this.disabled = true;
        } else {
          this.disabled = false;
        }
      },
    },
  },
  beforeMount() {
    this.gridOptions = {
      defaultColDef: {
        width: 150,
        resizable: true,
        floatingFilter: true,
      },
      components: {
      },
    };
    this.onGridReady = () => {
      let filters = document.querySelectorAll('.ag-input-field-input');
      filters.forEach(filter => {
        filter.placeholder = this.$t('network.search');
      });
    };
    this.frameworkComponents = {
      btnCellRenderer: BtnInviteDetails,
      btnDeleteCellRenderer: BtnDeleteInvite,
    };
    this.columnDefs = [
      {
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
          buttons: ['clear'],
        },
        headerCheckboxSelection: false,
        checkboxSelection: true,
        headerName: this.$t('network.name'),
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-user"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'name',
        maxWidth: 350,
        flex: 1,
      },
      {
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
          buttons: ['clear'],
        },
        headerName: this.$t('network.auth.email'),
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-at"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'email',
        cellRenderer: (field) => {
          return `<a href="mailto:${field.value}">${field.value}</a>`;
        },
        minWidth: 200,
        flex: 1
      },
      {
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
          buttons: ['clear'],
          textCustomComparator: function (filter, value, filterText) {
            return value.toLowerCase().includes(filterText.toLowerCase());
          },
        },
        headerName: 'Status',
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-user-clock"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'id',
        valueGetter: function(params) {
          if (params.data.accepted === true) {
            return 'Accepted';
          } else if (params.data.declined === true) {
            return 'Declined';
          } else {
            return 'Pending';
          }
        },
        minWidth: 100,
        flex: 1,
      },
      {
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
          buttons: ['clear'],
          textCustomComparator: function (filter, value, filterText) {
            return value.toLowerCase().includes(filterText.toLowerCase());
          },
        },
        headerName: 'Role',
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-users-class"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'is_admin',
        valueGetter: function(params) {
          if (params.data.is_admin === true) {
            return 'Admin';
          } else {
            return 'Member';
          }
        },
        minWidth: 100,
        flex: 1,
      },
      {
        sortable: true,
        headerName: 'Last invite',
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-calendar-day"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'updated_at',
        filter:'agDateColumnFilter',
        filterParams: {
          comparator:function (filterLocalDateAtMidnight, cellValue) {
            var dateAsString = cellValue.split("T");
            if (dateAsString == null) return -1;
            var dateParts  = dateAsString[0].split("-");
            var cellDate = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));

            if (filterLocalDateAtMidnight.getTime() == cellDate.getTime()) {
              return 0
            }

            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            }

            if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            }
          },
          browserDatePicker: true,
          suppressAndOrCondition: true,
          buttons: ['clear'],
        },
        cellRenderer: (field) => {
          return this.formatDate(field.value);
        },
        minWidth: 100,
        flex: 1,
      },
      {
        headerName:this.$t('network.member.invitationDetails'),
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-info-circle"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'id',
        cellRenderer: 'btnCellRenderer',
        cellRendererParams: {
          clicked: field => {
            this.showInviteModal(field);
          },
        },
        minWidth: 100,
        flex: 1,
      },
      {
        headerName: this.$t('network.member.message'),
        headerComponentParams: {
          template:
            '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-envelope"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
            '</div>'
        },
        field: 'email',
        cellRenderer: (field) => {
          const link = document.createElement('a');
          link.href = `mailto:${field.value}`;
          link.innerHTML = `<span class="text-primary fw-semibold fs-5">${this.$t('network.member.message')}</span>`;
          link.addEventListener('click', (event) => {
            event.stopPropagation();
            window.open(link.href, '_blank');
          });
          return link.outerHTML;
        },
        minWidth: 100,
        flex: 1,
      },
      {
        headerName: 'Delete',
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-trash-alt"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'id',
        cellRenderer: 'btnDeleteCellRenderer',
        cellRendererParams: {
          clicked: field => {
            this.deleteInvitation(field);
          },
        },
        minWidth: 100,
        flex: 1,
      },
      {
        sortable: true,
        filter: true,
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
          buttons: ['clear'],
        },
        headerName: 'Accepted by',
        headerComponentParams: {
          template:
          '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
            '    <i class="fas fa-check-to-slot"></i></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
          '</div>'
        },
        field: 'accepted_by',
        getQuickFilterText: () => '',
        minWidth: 200,
        flex: 1
      },
    ];
  },
  mounted() {
    this.load();
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    boolIconFormatter(v) {
      return v ? '<span  class="ml-5 badge badge-success"><i class="fa fa-check" /> Check</span>' : '';
      //<span lass="ml-5 badge badge-danger">Declined</span>
    },
    resend(member) {
      this.selectedUser = member;
      this.showResendModal = true;
    },
    load() {
      this.loadingInvites = true;
      this.$http
        .get('networks/' + this.network.slug + '/all-invites')
        .then(resp => {
          this.loadingInvites = false;
          this.invitedMembers = resp.data;
          this.filteredInvitedMembers = this.filteredMembers != null ? this.filteredMembers : resp.data;
        });
    },
    saveRecord(record) {
      this.loadingInvites = true;
      this.$http.put('network-user-invite/' + record.id, { params: record }).then(() => {
        this.loadingInvites = false;
      });
    },
    formatDate(date) {
      date = new Date(date);
      return ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2) + '-' + date.getFullYear();
    },
    onFilterTextBoxChanged() {
      this.gridApi.setQuickFilter(
        document.getElementById('filter-text-box-invited').value
      );
    },
    refreshFilterTextBox() {
      this.gridApi.setQuickFilter(
        document.getElementById('filter-text-box-invited').value = ''
      );
    },
    showInviteModal(member) {
      this.showingInviteModal = true;
      this.selectedMemberInvite = member;
    },
    deleteInvitation(invite) {
      if (this.isAdmin) {
        let action = () => {
          this.$http.delete('/networks/' + this.network.slug + '/invites/' + invite.token).then(() => {
            this.filteredInvitedMembers = this.filteredInvitedMembers.filter(m => m.token != invite.token);
            this.invitedMembers = this.invitedMembers.filter(m => m.token != invite.token);
          });
        };
        this.showConfirmPrompt(action);
      } else {
        this.$swal.fire({
          icon: 'error',
          title: "You don't have the permissions to do this.",
        });
      }
    },
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();
    },
    showInviteStatus(member){
      if (member.accepted) {
        return '<span class="text-success">Accepted<span>';
      } else if (member.declined) {
        return '<span class="text-danger">Declined<span>';
      } else {
        return '<span class="text-secondary">Pending<span>';
      }
    },
    // do the full resend (either all, or selected)
    resendAll(selected) {
      this.processingResend = true;
      this.$http
        .post('networks/' + this.network.slug + '/re-send-invites', {
          selectedRows: selected ? selected : this.selectedRows,
          inviteMessage: this.inviteMessage,
        })
        .then(resp => {
          this.processingResend = false;
          var num_resends = resp.data.num_resends;
          if (num_resends == 1) {
            this.$bvModal.msgBoxOk(`${num_resends} pending invitation was resent`);
          } else {
            this.$bvModal.msgBoxOk(`${num_resends} pending invitations were resent`);
          }

          this.showResendModal = false; // hide the resend invite dialog
          this.selectedRows = []; // clear selection
          this.load(); // load invites again
        })
        .catch(() => {
          this.$bvModal.msgBoxOk(
            this.$t('network.member.somethingWentWrong')
          );
        });
    },
  },
};
</script>
<style>
.ag-header-cell-label {
  justify-content: center;
  font-size: 19px;
  font-weight: 600;
  color: #5c6d80;
}
.ag-theme-alpine .ag-row {
  font-size: 15px;
}
</style>